import type { ComponentType } from 'react'
import { ArrowLeft, Calendar, Clock, Tag as TagIcon } from 'lucide-react'

import type { Post, Tag } from '@package/cms/types'
import { RichText } from '@ui/web/lib'
import { formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { AuthorHoverCard } from '../components/author-hover-card'

export interface BlogPageComponents {
  Link?: ComponentType<{ href: string; children: React.ReactNode; className?: string }>
  Image?: ComponentType<{
    src: string
    alt: string
    className?: string
    fill?: boolean
    sizes?: string
    priority?: boolean
    width?: number
    height?: number
  }>
}

export interface BlogPageProps {
  tag: Tag | Partial<Tag>
  post: Post | Partial<Post>
  /** When true, tag/post may be partial and all links are disabled (href="#") */
  preview?: boolean
  /** Optional root class (e.g. font from host) */
  rootClassName?: string
  /** Optional framework-specific Link/Image (e.g. Next.js) */
  components?: BlogPageComponents
}

export function BlogPage({ tag, post, preview = false, rootClassName, components = {} }: BlogPageProps) {
  const { Link: LinkComponent, Image: ImageComponent } = components
  const Link = LinkComponent ?? 'a'
  const tagSlug = tag?.slug ?? ''
  const author = post?.author
  const authorName = typeof author === 'string' ? 'Author' : (author?.name ?? 'Author')
  const rawAvatar = typeof author === 'string' ? null : author?.avatar
  const avatar = rawAvatar && typeof rawAvatar === 'object' && 'url' in rawAvatar ? rawAvatar : null
  const headerBlock = post?.header?.[0]
  const headerImage =
    headerBlock && typeof headerBlock.backgroundImage !== 'string' ? headerBlock.backgroundImage : null

  const formattedDate = post?.createdAt ? formatDate(post.createdAt, { dateStyle: 'long' }) : ''

  // Estimate reading time (rough: 150 words per minute)
  const contentText = post?.content ? JSON.stringify(post.content) : ''
  const wordCount = contentText.split(/\s+/).filter(Boolean).length
  const readingTime = Math.max(1, Math.ceil(wordCount / 150))

  const rawFeatureImage = post?.featureImage
  const featureImage =
    rawFeatureImage && typeof rawFeatureImage === 'object' && 'url' in rawFeatureImage ? rawFeatureImage : null

  const backHref = `/blog/${tagSlug}`
  const allPostsHref = '/blog'

  return (
    <main className={cn('srd:bg-neutrals-white srd:min-h-screen', rootClassName)}>
      {/* Header Image - Full Width */}
      {headerImage?.url && (
        <div className='srd:relative srd:h-[35vh] srd:w-full'>
          {ImageComponent ? (
            <ImageComponent
              src={headerImage.url}
              alt={headerImage.alt ?? post?.title ?? ''}
              fill
              className='srd:object-cover'
              priority
              sizes='100vw'
            />
          ) : (
            <div className='srd:absolute srd:inset-0 srd:h-full srd:w-full'>
              <img
                src={headerImage.url}
                alt={headerImage.alt ?? post?.title ?? ''}
                className='srd:size-full srd:object-cover'
              />
            </div>
          )}
          <div className='srd:absolute srd:inset-0 srd:bg-linear-to-t srd:from-neutrals-white srd:via-neutrals-white/20 srd:to-transparent' />

          {/* Back Button */}
          <div className='srd:absolute srd:left-6 srd:top-6'>
            <Link
              href={preview ? '#' : backHref}
              className='srd:inline-flex srd:items-center srd:gap-2 srd:rounded-full srd:border srd:border-neutrals-n-20 srd:bg-neutrals-white/50 srd:px-4 srd:py-2 srd:text-sm srd:font-medium srd:text-complementary-black srd:backdrop-blur-sm srd:transition-colors srd:hover:bg-neutrals-white'
            >
              <ArrowLeft className='srd:h-4 srd:w-4' />
              Kembali ke {tag?.name ?? ''}
            </Link>
          </div>

          {/* Featured Badge */}
          {post?.featured && (
            <div className='srd:absolute srd:right-6 srd:top-6'>
              <span className='srd:rounded-full srd:bg-complementary-yellow-100 srd:px-4 srd:py-1.5 srd:text-sm srd:font-bold srd:uppercase srd:tracking-wide srd:text-complementary-black'>
                Featured
              </span>
            </div>
          )}
        </div>
      )}

      {/* No Header Image - Show Back Button */}
      {!headerImage?.url && (
        <div className='srd:mx-auto srd:max-w-4xl srd:px-4 srd:pt-8'>
          <Link
            href={preview ? '#' : backHref}
            className='srd:inline-flex srd:items-center srd:gap-2 srd:text-sm srd:text-neutrals-n-75 srd:transition-colors srd:hover:text-complementary-black'
          >
            <ArrowLeft className='srd:h-4 srd:w-4' />
            Kembali ke {tag?.name ?? ''}
          </Link>
        </div>
      )}

      {/* Content */}
      <article className='srd:mx-auto srd:max-w-4xl srd:px-4 srd:py-12'>
        {/* Meta Info */}
        <header className='srd:mb-10'>
          {/* Tag */}
          <Link
            href={preview ? '#' : backHref}
            className='srd:mb-4 srd:inline-flex srd:items-center srd:gap-1.5 srd:rounded-full srd:bg-main-blue-100/20 srd:px-3 srd:py-1 srd:text-sm srd:font-medium srd:text-main-blue-100 srd:transition-colors srd:hover:bg-main-blue-100/30'
          >
            <TagIcon className='srd:h-3 srd:w-3' />
            {tag?.name ?? ''}
          </Link>

          {/* Title */}
          <h1 className='srd:mb-6 srd:text-3xl srd:font-bold srd:leading-tight srd:text-complementary-black srd:md:text-4xl srd:lg:text-5xl'>
            {post?.title ?? ''}
          </h1>

          {/* Description */}
          {post?.description && (
            <p className='srd:mb-8 srd:text-lg srd:leading-relaxed srd:text-neutrals-n-75'>{post.description}</p>
          )}

          {featureImage?.url && (
            <figure className='srd:my-6'>
              <div className='srd:relative srd:aspect-video srd:w-full srd:overflow-hidden srd:rounded-lg'>
                {ImageComponent ? (
                  <ImageComponent
                    src={featureImage.url}
                    alt={featureImage.alt ?? post?.title ?? ''}
                    fill
                    className='srd:object-cover'
                    sizes='(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 700px'
                  />
                ) : (
                  <img
                    src={featureImage.url}
                    alt={featureImage.alt ?? post?.title ?? ''}
                    className='srd:size-full srd:object-cover'
                  />
                )}
              </div>
              {featureImage.alt && (
                <figcaption className='srd:mt-2 srd:text-center srd:text-sm srd:text-neutrals-n-75'>
                  {featureImage.alt}
                </figcaption>
              )}
            </figure>
          )}

          {/* Author & Meta */}
          <div className='srd:flex srd:flex-wrap srd:items-center srd:gap-6 srd:border-b srd:border-neutrals-n-20 srd:pb-6'>
            {typeof author === 'object' && author !== null ? (
              <AuthorHoverCard author={author}>
                <div className='srd:flex srd:cursor-pointer srd:items-center srd:gap-3'>
                  {avatar?.url ? (
                    ImageComponent ? (
                      <ImageComponent
                        src={avatar.url}
                        alt={authorName}
                        width={40}
                        height={40}
                        className='srd:h-10 srd:w-10 srd:rounded-full srd:object-cover'
                      />
                    ) : (
                      <img
                        src={avatar.url}
                        alt={authorName}
                        width={40}
                        height={40}
                        className='srd:h-10 srd:w-10 srd:rounded-full srd:object-cover'
                      />
                    )
                  ) : (
                    <div className='srd:flex srd:h-10 srd:w-10 srd:items-center srd:justify-center srd:rounded-full srd:bg-main-blue-100'>
                      <span className='srd:font-bold srd:text-neutrals-white'>{authorName.charAt(0) || '?'}</span>
                    </div>
                  )}
                  <p className='srd:font-medium srd:text-complementary-black'>{authorName}</p>
                </div>
              </AuthorHoverCard>
            ) : (
              <div className='srd:flex srd:items-center srd:gap-3'>
                <div className='srd:flex srd:h-10 srd:w-10 srd:items-center srd:justify-center srd:rounded-full srd:bg-main-blue-100'>
                  <span className='srd:font-bold srd:text-neutrals-white'>{authorName.charAt(0) || '?'}</span>
                </div>
                <p className='srd:font-medium srd:text-complementary-black'>{authorName}</p>
              </div>
            )}

            <div className='srd:flex srd:items-center srd:gap-4 srd:text-sm srd:text-neutrals-n-75'>
              <span className='srd:flex srd:items-center srd:gap-1.5'>
                <Calendar className='srd:h-4 srd:w-4' />
                {formattedDate}
              </span>
              <span className='srd:flex srd:items-center srd:gap-1.5'>
                <Clock className='srd:h-4 srd:w-4' />
                {readingTime} min
              </span>
            </div>
          </div>
        </header>

        {/* Rich Text Content */}
        {post?.content && (
          <div className='srd:prose srd:prose-lg srd:max-w-none'>
            <RichText data={post.content} />
          </div>
        )}

        {/* Footer */}
        <footer className='srd:mt-16 srd:border-t srd:border-neutrals-n-20 srd:pt-8'>
          <div className='srd:flex srd:flex-col srd:items-start srd:justify-between srd:gap-4 srd:sm:flex-row srd:sm:items-center'>
            <Link
              href={preview ? '#' : backHref}
              className='srd:inline-flex srd:items-center srd:gap-2 srd:text-main-blue-100 srd:transition-colors srd:hover:text-main-blue-75'
            >
              <ArrowLeft className='srd:h-4 srd:w-4' />
              Lihat lebih banyak postingan {tag?.name ?? ''}
            </Link>

            <Link
              href={preview ? '#' : allPostsHref}
              className='srd:text-sm srd:text-neutrals-n-75 srd:transition-colors srd:hover:text-complementary-black'
            >
              Lihat semua postingan →
            </Link>
          </div>
        </footer>
      </article>
    </main>
  )
}
